import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Wrench, X } from "lucide-react";
import type {
  TrainingAttempt,
  TrainingNextLevel,
} from "@/lib/api/training-simulator/training-simulator.ts";
import {
  useDevTrainingAction,
  type DevTrainingAction,
} from "@/lib/api/training-simulator/training-simulator-dev.ts";
import { WELCOME_STORAGE_KEY } from "@/lib/training-simulator/constants.ts";
import { cn } from "@/lib/utils.ts";
import { TrainingWelcomeModal } from "../session/training-welcome-modal.tsx";
import { TrainingLevelContextModal } from "../session/training-level-context-modal.tsx";
import { TrainingLevelCompleteModal } from "../session/training-level-complete-modal.tsx";
import { TrainingEscalationModal } from "../session/training-escalation-modal.tsx";
import { TrainingLeadMentorModal } from "../session/training-lead-mentor-modal.tsx";

type PreviewModal =
  | "none"
  | "welcome"
  | "context"
  | "complete"
  | "escalation"
  | "leadMentor";

interface TrainingDevPanelProps {
  attempt?: TrainingAttempt;
  nextLevel?: TrainingNextLevel | null;
  currentAttemptId?: string | null;
  onToggleReplyFailed?: () => void;
}

const isTrainingDevPanelEnabled =
  import.meta.env.DEV ||
  import.meta.env.VITE_STYTCH_PUBLIC_ENV !== "production";

export function TrainingDevPanel({
  attempt,
  nextLevel,
  currentAttemptId,
  onToggleReplyFailed,
}: TrainingDevPanelProps) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<PreviewModal>("none");
  const { mutate: runDevAction, isPending } = useDevTrainingAction();
  const navigate = useNavigate();

  if (!isTrainingDevPanelEnabled) return null;

  const close = () => setPreview("none");

  const completeAttemptId = attempt?.id ?? currentAttemptId ?? undefined;
  const previewLevelPosition = attempt?.level.position ?? 1;
  const previewModulePosition = attempt?.level.module.position ?? 1;
  const previewModuleTitle = attempt?.level.module.title ?? "The first hello";
  const previewContextCopy =
    attempt?.level.context_copy ??
    "Sample level context shown here for previewing the modal.";
  const previewNextLevel: TrainingNextLevel = nextLevel ?? {
    id: "preview-next",
    position: previewLevelPosition + 1,
    title: "A friendly hi",
  };

  const handleAction = (action: DevTrainingAction) => {
    runDevAction({ action, attemptId: completeAttemptId });
  };

  return (
    <>
      <div className="fixed bottom-4 left-[110px] z-50 flex flex-col items-start gap-2">
        {open && (
          <div className="flex w-64 flex-col gap-3 rounded-md border border-surfaces-divider bg-surfaces-z0 p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <span className="text-header-6 text-text-primary">Dev tools</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close dev tools"
                className="text-icon-tertiary hover:text-icon-primary"
              >
                <X className="size-4" />
              </button>
            </div>

            <DevSection label="Preview UI">
              <DevButton onClick={() => setPreview("welcome")}>
                Welcome modal
              </DevButton>
              <DevButton onClick={() => setPreview("context")}>
                Level context modal
              </DevButton>
              <DevButton onClick={() => setPreview("complete")}>
                Level complete modal
              </DevButton>
              <DevButton
                disabled={!completeAttemptId}
                onClick={() =>
                  completeAttemptId &&
                  navigate({
                    to: "/training/complete/$attemptId",
                    params: { attemptId: completeAttemptId },
                    search: { screen: "module" },
                  })
                }
              >
                Module complete screen
              </DevButton>
              <DevButton
                disabled={!completeAttemptId}
                onClick={() =>
                  completeAttemptId &&
                  navigate({
                    to: "/training/complete/$attemptId",
                    params: { attemptId: completeAttemptId },
                    search: { screen: "simulator" },
                  })
                }
              >
                All modules complete screen
              </DevButton>
              <DevButton onClick={() => setPreview("escalation")}>
                Escalation modal
              </DevButton>
              <DevButton onClick={() => setPreview("leadMentor")}>
                Lead mentor modal
              </DevButton>
              {onToggleReplyFailed && (
                <DevButton onClick={onToggleReplyFailed}>
                  Toggle reply-failed
                </DevButton>
              )}
            </DevSection>

            <DevSection label="Server actions">
              <DevButton
                disabled={!completeAttemptId || isPending}
                onClick={() => handleAction("complete_attempt")}
              >
                Force-complete attempt
              </DevButton>
              <DevButton
                disabled={isPending}
                onClick={() => handleAction("reset")}
              >
                Reset progress
              </DevButton>
              <DevButton
                disabled={isPending}
                onClick={() => handleAction("reset_modules")}
              >
                Reset all modules
              </DevButton>
              {isPending && (
                <span className="flex items-center gap-2 text-body-3 text-text-secondary">
                  <Loader2 className="size-4 animate-spin" />
                  Running...
                </span>
              )}
            </DevSection>

            <DevSection label="Local">
              <DevButton
                onClick={() =>
                  window.localStorage.removeItem(WELCOME_STORAGE_KEY)
                }
              >
                Reset welcome flag
              </DevButton>
            </DevSection>
          </div>
        )}

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-label="Toggle training dev tools"
          className="flex size-12 items-center justify-center rounded-full bg-brand-trueBlack text-surfaces-z0 shadow-lg transition-transform hover:scale-105"
        >
          <Wrench className="size-5" />
        </button>
      </div>

      <TrainingWelcomeModal
        open={preview === "welcome"}
        onAcknowledge={close}
      />
      <TrainingLevelContextModal
        open={preview === "context"}
        contextCopy={previewContextCopy}
        onAcknowledge={close}
      />
      <TrainingLevelCompleteModal
        open={preview === "complete"}
        levelPosition={previewLevelPosition}
        modulePosition={previewModulePosition}
        moduleTitle={previewModuleTitle}
        nextLevel={previewNextLevel}
        isContinuing={false}
        isRetrying={false}
        onContinue={close}
        onRetry={close}
        onBackToTraining={close}
      />
      <TrainingEscalationModal
        open={preview === "escalation"}
        onClose={close}
      />
      <TrainingLeadMentorModal
        open={preview === "leadMentor"}
        onOpenChange={(next) => !next && close()}
      />
    </>
  );
}

function DevSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-body-4 uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      {children}
    </div>
  );
}

function DevButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "rounded-md border border-surfaces-divider bg-surfaces-z2 px-3 py-2 text-left text-body-3 text-text-primary transition-colors hover:bg-surfaces-z3",
        "disabled:cursor-not-allowed disabled:opacity-50",
      )}
    >
      {children}
    </button>
  );
}
